<template>
  <div class="mb-10 2xs:mb-2 sm:mb-0 recipients-list">
    <aha-spinner class="py-20" v-if="isTeamMembersLoading" />

    <p class="py-20 text-center" v-else-if="hasErrorOnLoadingTeamMembers">
      {{ $i18nPresenter.t('Cannot retrieve your recipient list.') }}
      <br />
      Please <a @click.stop="getTeamMemberList()" class="cursor-pointer primary-color">
        click here
      </a> to try again
    </p>

    <template v-else>
      <main class="overflow-auto max-h-72">
        <table class="table-recipients">
          <thead>
            <tr>
              <th class="w-7">
                <aha-checkbox :checked="isAllTeamMembersSelected" @change="handleCheckAll" />
              </th>
              <th class="w-32">
                {{ $i18nPresenter.t('Members') }}
              </th>
              <th class="text-left">
                {{ $i18nPresenter.t('Email') }}
              </th>
              <th class="w-10" />
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(member, index) in teamMembers"
              :key="`member-${member.id}`"
            >
              <td>
                <aha-checkbox
                  :checked="member.selected"
                  @change="toggleSelectRecipient(index)"
                />
              </td>
              <td class="truncate text-base-90">
                <span class="text-base-70">{{ index + 1 }}.</span> {{ member.name }}
              </td>
              <td class="truncate text-base-80">
                {{ member.email }}
              </td>
              <td>
              </td>
            </tr>
            <tr v-if="!teamMembers || teamMembers.length === 0">
              <td colspan="4" class="text-center bg-base-0">
                {{ $i18nPresenter.t('No team members found') }}
              </td>
            </tr>
          </tbody>
        </table>
      </main>
      <hr class="my-3 text-base-30" />
      <footer class="relative h-10 mb-3">
        <p v-if="numberOfSelectedTeamMembers > 0" class="absolute left-0 py-2">
          <span class="mr-0.5">
            {{ numberOfSelectedTeamMembers }}
            {{ $i18nPresenter.t('selected') }}
            {{ numberOfSelectedTeamMembers === 1 ? 'recipient' : 'recipients' }}
          </span>
          <a @click="deselectRecipients()" class="text-base-60 text-link">
            <aha-icon
              name="close"
              width="16"
              height="16"
              viewBox="0 0 24 24"
            />
          </a>
        </p>

        <vue-recaptcha
          :sitekey="keyRecaptcha"
          @verify="onVerify"
          @error="onRecaptchaError"
          size="invisible"
          @expired="onExpired"
          :load-recaptcha-script="true"
          recaptcha-host="www.recaptcha.net"
          ref="recaptcha"
          class="auth-captcha"
        />

        <aha-button
          @click="onSubmit"
          class="absolute right-0"
          :submitting="sharingPresentationToCollaborate"
          :disabled="numberOfSelectedTeamMembers === 0"
        >
          {{ $i18nPresenter.t('Invite') }}
        </aha-button>
      </footer>
    </template>
  </div>
</template>

<script>
import VueRecaptcha from 'vue-recaptcha'

import { mapState } from 'vuex'
import configs from '@/config'

import PresentationApi from '@/api/presentation'
import UserApi from '@/api/user'

// utils
import utils from '@/util'

import constants from '@/constant'

const { eventCategories } = constants

export default {
  components: {
    VueRecaptcha,
  },
  props: {
    presentation: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      keyRecaptcha: configs.keyRecapcha,
      tokenRecaptcha: '',
      teamMembers: [],
      sharingPresentationToCollaborate: false,
      isTeamMembersLoading: false,
      hasErrorOnLoadingTeamMembers: false,
    }
  },
  computed: {
    ...mapState({
      currentUser: (state) => state.currentUser,
    }),
    isAllTeamMembersSelected() {
      return this.teamMembers.every((user) => user.selected)
    },
    numberOfSelectedTeamMembers() {
      return this.teamMembers.filter((user) => user.selected).length
    },
  },
  async mounted() {
    this.getTeamMemberList()
  },
  methods: {
    onVerify(response) {
      this.tokenRecaptcha = response
      this.onClickShare()
    },
    onRecaptchaError() {
      this.sharingPresentationToCollaborate = false
      this.textError = this.$i18nPresenter.t('Recaptcha error. If the problem still occurs, please check your internet connection.')
    },
    onSubmit() {
      if (this.sharingPresentationToCollaborate) return
      this.sharingPresentationToCollaborate = true
      if (this.currentUser.email.match(/qa\.ahaslides\+(.*)@gmail.com/i)) {
        this.onClickShare()
      } else {
        this.$refs.recaptcha.execute()
        utils.removeRecaptchaCover()
      }
    },
    onExpired() {
      this.$refs.recaptcha.reset()
      this.tokenRecaptcha = ''
    },
    async getTeamMemberList() {
      this.isTeamMembersLoading = true
      this.hasErrorOnLoadingTeamMembers = false

      try {
        const res = await UserApi.getTeamMembers({
          userId: this.currentUser.id,
          page: 1,
          size: 500,
        })
        const teamMembers = res.users.map((user) => {
          return {
            name: `${user.firstName} ${user.lastName}`,
            email: user.email,
            id: user.id,
          }
        }).filter((user) => user.email !== this.currentUser.email)
        this.teamMembers = teamMembers.filter((user) => user.email !== this.currentUser.email)
      } catch (error) {
        this.isTeamMembersLoading = false
        this.hasErrorOnLoadingTeamMembers = true
        throw error
      }
      this.isTeamMembersLoading = false
    },
    toggleSelectRecipient(index) {
      const newList = [...this.teamMembers]
      newList[index].selected = !newList[index].selected
      this.teamMembers = newList
    },
    handleCheckAll() {
      this.teamMembers = this.teamMembers.map((member) => ({
        ...member,
        selected: !this.isAllTeamMembersSelected,
      }))
    },
    async onClickShare() {
      let gaCategoryType = eventCategories.PRESENTATION_EDITOR
      if (this.$route.name === 'Presentations' || this.$route.name === 'FolderDetail') {
        gaCategoryType = eventCategories.PRESENTATION_LIST
      }
      utils.mixpanelTracking(gaCategoryType, 'Presentation > Invite to edit > Submit')
      const collaborators = this.teamMembers
        .filter((user) => user.selected)
        .map((user) => user.email)
      this.sharingPresentationToCollaborate = false

      if (!collaborators.length) return
      this.sharingPresentationToCollaborate = true

      try {
        const params = {
          collaborators,
          tokenRecaptcha: this.tokenRecaptcha,
        }

        await PresentationApi.addCollaborators(this.presentation.id, params)
        utils.mixpanelTracking(gaCategoryType, 'Presentation > Invite to edit > Succeeded')
        utils.showToastSuccess(`${this.$i18nPresenter.t('Successfully invited')} ${collaborators.length} ${collaborators.length === 1 ? this.$i18nPresenter.t('member') : this.$i18nPresenter.t('members')} ${this.$i18nPresenter.t('to edit this presentation.')}`)

        this.deselectRecipients()
      } catch {
        utils.mixpanelTracking(gaCategoryType, 'Presentation > Invite to edit > Failed')
        utils.showToastError(`${this.$i18nPresenter.t('Failed to invite')} ${collaborators.length} ${collaborators.length === 1 ? this.$i18nPresenter.t('member') : this.$i18nPresenter.t('members')} ${this.$i18nPresenter.t('to edit this presentation.')}`)
      }

      this.sharingPresentationToCollaborate = false
      this.$refs.recaptcha.reset()
      this.tokenRecaptcha = ''
    },
    deselectRecipients() {
      this.teamMembers = this.teamMembers.map((member) => ({
        ...member,
        selected: false,
      }))
    },

  },
}
</script>

<style lang="scss" scoped>
  .table-recipients {
    @apply border-collapse table-fixed w-full;

    th, td {
      @apply p-2;
    }

    th:nth-child(2), td:nth-child(2) {
      @apply pl-0 text-left;
    }

    tbody > tr:nth-child(odd) {
      @apply bg-secondary-10;

      > td:first-child {
        @apply rounded-l-sm;
      }

      > td:last-child {
        @apply rounded-r-sm;
      }
    }
  }
</style>
